'use client'

import { useRef } from 'react'
import LocationSearch from '../LocationSearch'
import {
  LayersIcon,
  MapPin,
  RotateCcwIcon,
  Search,
  StarIcon,
  UserCheck2Icon,
  Video,
} from 'lucide-react'
import { AnimatePresence } from 'motion/react'

import { RevealSection, RevealUl } from '@/components/RevealSection'
import { TabSelect } from '@/components/TabSelect'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useEventsContext } from '@/providers/EventsProvider'

import FilterButton from './components/FilterButton'

const VIEW_TABS = [
  {
    name: 'All',
    value: 'all',
    icon: <LayersIcon className="size-4" />,
  },
  {
    name: 'Official',
    value: 'official',
    icon: <StarIcon className="size-4" />,
  },
  {
    name: 'Unofficial',
    value: 'unofficial',
    icon: <UserCheck2Icon className="size-4" />,
  },
]

const FORMAT_TABS = [
  {
    name: 'All',
    value: 'all',
    icon: <LayersIcon className="size-4" />,
  },
  {
    name: 'In-Person',
    value: 'in-person',
    icon: <MapPin className="size-4" />,
  },
  {
    name: 'Virtual',
    value: 'virtual',
    icon: <Video className="size-4" />,
  },
]

const FilterInner = () => {
  const {
    eventTopicData,
    searchTerm,
    setSearchTerm,
    selectedTopics,
    toggleTopic,
    clearTopics,
    view,
    setView,
    format,
    setFormat,
  } = useEventsContext()
  const searchInputRef = useRef<HTMLInputElement>(null)

  return (
    <RevealSection stagger className="flex w-full shrink-0 flex-col gap-4">
      <Input
        ref={searchInputRef}
        placeholder="Search events"
        aria-label="Search events"
        autoComplete="off"
        IconComponent={Search}
        className="w-full items-center font-body"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onClear={() => setSearchTerm('')}
      />
      {format !== 'virtual' && <LocationSearch />}
      <div className="flex flex-col gap-2">
        <h3 className="font-family-body text-base font-semibold text-muted-foreground">Class</h3>
        <TabSelect
          instanceKey="events-explore-view"
          size="small"
          tabs={VIEW_TABS}
          value={view}
          onTabChange={(value) => {
            const nextView = value as 'all' | 'official' | 'unofficial'
            if (nextView === view) return
            setView(nextView)
          }}
        />
      </div>
      <div className="flex flex-col gap-2">
        <h3 className="font-family-body text-base font-semibold text-muted-foreground">Venue</h3>
        <TabSelect
          instanceKey="events-explore-format"
          size="small"
          tabs={FORMAT_TABS}
          value={format}
          onTabChange={(value) => {
            const nextFormat = value as 'all' | 'in-person' | 'virtual'
            if (nextFormat === format) return
            setFormat(nextFormat)
          }}
        />
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2">
          <h3 className="font-family-body text-base font-semibold text-muted-foreground">Topic</h3>
          <Button
            variant="iconOutline"
            size="small"
            customIcon={<RotateCcwIcon className="size-4.5" />}
            title="Clear filters"
            onClick={clearTopics}
            disabled={selectedTopics.length === 0}
          />
        </div>
        <AnimatePresence mode="wait">
          {eventTopicData.length > 0 ? (
            <RevealUl key={view} stagger className="flex flex-col gap-2">
              {eventTopicData.map((topic) => (
                <div key={topic.id} className="w-full">
                  <FilterButton
                    topic={topic}
                    handleTopicClick={toggleTopic}
                    activeTopics={selectedTopics}
                  />
                </div>
              ))}
            </RevealUl>
          ) : (
            <p className="text-sm text-muted-foreground">No topics available for this view.</p>
          )}
        </AnimatePresence>
      </div>
    </RevealSection>
  )
}

export default FilterInner
